import { For, Grid, Text, VStack } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole, selectAllGroups } from '@redux/authent/authent.selectors';
import { FormikProps } from 'formik';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import { Group, NormalizedGroup } from '@/types/authent/groups.types';
import { User } from '@/types/authent/login.types';
import { RoleEnum } from '@/types/authent/roles.types';

import GroupCard from './GroupCard';
import GroupLightCard from './GroupLightCard';

type Props = {
  formik: FormikProps<Partial<User>>;
  activeGroup?: Group | null;
};

export default function UserGroups({ formik, activeGroup }: Readonly<Props>) {
  const groups = useAppSelector(selectAllGroups);
  const canAccessGroup = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_GROUP));
  const handleChange = (group: NormalizedGroup) => {
    if (!!formik.values.groups?.some((element) => element.code === group.code)) {
      formik.setFieldValue(
        'groups',
        formik.values.groups.filter((element) => element.code !== group.code),
      );
    } else {
      const newGroupLight = { id: group.id, code: group.code, name: group.name };
      formik.setFieldValue('groups', formik.values.groups?.concat(newGroupLight) ?? [newGroupLight]);
    }
  };

  return (
    <VStack paddingTop={8} width="100%" height="100%" gap={5}>
      {(canAccessGroup && groups.length === 0) || (!canAccessGroup && formik.initialValues.groups!.length) === 0 ? (
        <Text textStyle="xl" marginY="auto">
          <FormattedMessage id="components.usersManagement.users.groups.empty" values={{ editable: canAccessGroup }} />
        </Text>
      ) : (
        <>
          <Text textStyle="xl">
            <FormattedMessage id="components.usersManagement.users.groups.title" />
          </Text>
          <Grid templateColumns="repeat(3, 1fr)" gap={2}>
            {canAccessGroup ? (
              <For each={groups}>
                {(group, index) => (
                  <Fragment key={index}>
                    <GroupCard
                      group={group}
                      isSelected={!!formik.values.groups?.some((element) => element.code === group.code)}
                      isActiveGroup={group.code === activeGroup?.code}
                      handleChange={handleChange}
                    />
                  </Fragment>
                )}
              </For>
            ) : (
              <For each={formik.initialValues.groups}>
                {(group, index) => (
                  <Fragment key={index}>
                    <GroupLightCard group={group} isActiveGroup={group.code === activeGroup?.code} />
                  </Fragment>
                )}
              </For>
            )}
          </Grid>
        </>
      )}
    </VStack>
  );
}
